import { type FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import { useHomePageContext } from '../HomePageContext';
import { HpSharedShopByModuleItemVariant } from './HpSharedShopByModuleItemVariant';

import { type HpSharedShopByModuleVariant_viewer$key } from './__generated__/HpSharedShopByModuleVariant_viewer.graphql';
import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment HpSharedShopByModuleVariant_viewer on Viewer {
        trendingCreators {
            category
            ...HpSharedShopByModuleItemVariant_trendingCreator
        }
    }
`;

function orderByList<T extends Readonly<{ readonly category: string | null } | null>>({
    items,
    orderList,
}: {
    items: ReadonlyArray<T>;
    orderList: string[];
}): T[] {
    // 1) Bucket items by category
    const buckets = new Map<string, T[]>();
    items.forEach(item => {
        if (!item?.category) {
            return;
        }

        if (!buckets.has(item.category)) {
            buckets.set(item.category, []);
        }
        buckets.get(item.category)!.push(item);
    });

    // 2) Build result by pulling one object per entry in orderList
    return orderList.map(category => buckets.get(category)?.shift()).filter(Boolean) as T[];
}

export const HpSharedShopByModuleVariant: FC<{
    viewer: HpSharedShopByModuleVariant_viewer$key;
}> = ({ viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const { isMobile } = useHomePageContext();

    if (!viewer.trendingCreators?.length) {
        return null;
    }

    //Items returned from the graphql are not sorted by product requrements.
    //We need to sort them by the order of the categories in the orderList.
    const orderList = isMobile ? ['A', 'F', 'J', 'V'] : ['A', 'A', 'F', 'J', 'V'];
    const sortedCreators = orderByList({ items: viewer.trendingCreators, orderList });

    return (
        <div
            className={classnames('primary-viewport-width', dibsCss.bgMoonstone, {
                [dibsCss.mtLarge]: !isMobile,
            })}
        >
            <div
                className={classnames('primary-page-width', {
                    [dibsCss.pyLarge]: isMobile,
                    [dibsCss.pyLarger]: !isMobile,
                })}
            >
                <div
                    className={classnames(dibsCss.textCenter, {
                        [dibsCss.mbMedium]: isMobile,
                        [dibsCss.sassyFontHeaderLarge]: isMobile,
                        [dibsCss.mbLarge]: !isMobile,
                        [dibsCss.sassyFontHeaderXLarge]: !isMobile,
                    })}
                >
                    <FormattedMessage
                        id="HpSharedShopByModuleVariant.trendingCreators"
                        defaultMessage="Trending Creators"
                    />
                </div>

                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.justifyBetween,
                        dibsCss.itemsStart,
                        dibsCss.gapSmall,
                        { [dibsCss.flexWrap]: isMobile }
                    )}
                >
                    {sortedCreators.map((trendingCreator, index) => (
                        <HpSharedShopByModuleItemVariant
                            key={index}
                            trendingCreator={trendingCreator}
                            position={index + 1}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};
